import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { dataTableMeasuresSelector } from '../../../../redux/selectors/builder';
import { MAX_MEASURE_ROWS } from '../../../../config/constants';
import * as BuilderUI from '../../../../components/Builder/BuilderUI';
import { RowType } from '../../../../redux/slices/oldBuilder';
import MeasureRow from '../../../../components/Builder/Measures/MeasureRow';
import { BuilderAddRowAction } from '../../../../components/Builder/BuilderActions';
import DndDroppableZone from '../../../../components/DndEntity/DndDroppableZone';
import DropzoneArea from '../../../../components/DndEntity/DndDroppableZone/DropzoneArea';
import { useDndEntityInternalContext } from '../../../../components/DndEntity';
import { EntityDropzoneType } from '../../../../components/QuickAccessDictionary/DictionaryTabs/types';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../../components/DndEntity/validation';
import { isEmptyEventConditions } from '../../../../utils/builder';
import { getDndActiveItemData } from '../../../../components/DndEntity/utils';

const DataTableMeasures = () => {
  const dataTableMeasures = useSelector(dataTableMeasuresSelector);
  const { activeItem } = useDndEntityInternalContext();

  const canAddMeasure = dataTableMeasures.length < MAX_MEASURE_ROWS;

  const canDropMeasure = useMemo(() => {
    if (activeItem && canAddMeasure) {
      const data = getDndActiveItemData(activeItem);
      if (data) {
        return (
          data.__typename === 'TagLeafSimpleMeasure' ||
          data.__typename === 'TagLeafCompoundMeasure'
        );
      }
    }
    return false;
  }, [activeItem, canAddMeasure]);

  const canDropEvent = useMemo(() => {
    if (activeItem && canAddMeasure) {
      const allEventConditionsFilled = dataTableMeasures.every(
        (dataTableMeasure) => {
          return !isEmptyEventConditions(dataTableMeasure.eventConditions);
        },
      );

      const data = getDndActiveItemData(activeItem);
      if (allEventConditionsFilled && data) {
        return (
          data.__typename === 'TagLeafAnyEvent' ||
          data.__typename === 'TagLeafBasicEvent' ||
          data.__typename === 'TagLeafVirtualEvent'
        );
      }
    }
    return false;
  }, [activeItem, canAddMeasure, dataTableMeasures]);

  const measuresDropzoneId = `new-measure-droppable-${dataTableMeasures.length}`;
  const eventsDropzoneId = `new-measure-event-droppable-${dataTableMeasures.length}`;

  return (
    <BuilderUI.SectionColumn>
      <Droppable droppableId={RowType.dataTableMeasures} direction="vertical">
        {(provided) => (
          <BuilderUI.SectionColumn
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {dataTableMeasures.map((numerator, index) => (
              <Fragment key={`data-table-measure-${index}`}>
                <Draggable
                  isDragDisabled={dataTableMeasures.length === 1}
                  draggableId={RowType.dataTableMeasures + index}
                  index={index}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <MeasureRow
                        aliasId={numerator.userAliasId}
                        rowMeta={{
                          rowIndex: index,
                          rowType: RowType.dataTableMeasures,
                        }}
                        dragHandleProps={provided.dragHandleProps}
                        hideContent={Boolean(numerator?.measureId)}
                      />
                    </div>
                  )}
                </Draggable>
              </Fragment>
            ))}
            {provided.placeholder}
          </BuilderUI.SectionColumn>
        )}
      </Droppable>
      {canAddMeasure && !canDropMeasure && !canDropEvent && (
        <BuilderAddRowAction
          rowMeta={{
            rowType: RowType.dataTableMeasures,
            rowIndex: dataTableMeasures.length - 1,
          }}
          label={'+ Add Measure'}
          trackContext="builder"
          trackAction="add measure"
        />
      )}
      {canDropMeasure && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Measures]}
          id={measuresDropzoneId}
          data={
            {
              id: measuresDropzoneId,
              type: EntityDropzoneDataTypeEnum.Measures,
              rowMeta: {
                rowType: RowType.dataTableMeasures,
                rowIndex: dataTableMeasures.length,
              },
            } satisfies IMeasureEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">+ Add New Measure</DropzoneArea>
        </DndDroppableZone>
      )}
      {canDropEvent && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Events]}
          id={eventsDropzoneId}
          data={
            {
              id: eventsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: {
                rowType: RowType.dataTableMeasures,
                rowIndex: dataTableMeasures.length,
                eventIndex: 0,
              },
              isNewRow: true,
              shouldAddMeasureFunction: true,
            } satisfies IEventEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">+ Add Event as New Measure</DropzoneArea>
        </DndDroppableZone>
      )}
    </BuilderUI.SectionColumn>
  );
};

export default DataTableMeasures;
